import { css } from '@emotion/react';
import { Outlet, useLocation } from 'react-router-dom';

import useMinWidth from '~/common/hooks/useMinWidth.ts';
import BottomTab from '~/common/layouts/BottomTab.tsx';
import GlobalNav from '~/common/layouts/GlobalNav.tsx';
import { breakpoint } from '~/common/utils/breakpoint.utils.ts';
import { APP_FRAME_CSS, TOKEN } from '~/configs/theme.ts';

/**
 * 소비자 앱 셸.
 *
 * @description 폭에 따라 **네비게이션의 종류 자체가 바뀐다**.
 *  - lg(1024px) 미만: 지금까지와 같다 — 중앙 정렬 프레임 + 하단 탭.
 *  - lg 이상: 하단 탭을 내리고 상단 전역 네비게이션을 올린다.
 *
 *  둘을 다 그려놓고 CSS 로 감추지 않는다 — 안읽음 배지가 두 벌 구독되고, 스크린리더에는 존재하지
 *  않는 네비게이션이 두 개로 읽힌다. 폭 판정은 `useMinWidth` 한 곳에서만 한다.
 *
 *  본문 폭(APP_FRAME)은 이 단계에서 건드리지 않는다. 셸 교체와 폭 확장을 한 번에 하면 어느 쪽이
 *  깨뜨렸는지 가려지지 않는다.
 *
 *  각 페이지는 자체 헤더(AppHeader)를 계속 렌더한다 — 전역 네비게이션은 그 위에 얹히는 별개 층이다.
 */
/**
 * 프레임 폭 제한을 벗어나는 화면.
 *
 * @description 소비자 화면은 1120px 로 묶는다 — 글줄이 길어지면 읽기 나빠진다. 하지만 콘솔은
 *  읽는 화면이 아니라 **표를 훑는 화면**이라 기준이 반대다. 열이 여섯이면 1120 안에서는 칸마다
 *  글자가 끊기고 가로 스크롤이 생긴다(실제로 그랬다). 콘솔만 화면 폭을 그대로 쓴다.
 */
function isConsolePath(pathname: string) {
  return pathname.startsWith('/tutor-console') || pathname.startsWith('/admin');
}

function AppLayout() {
  const isDesktop = useMinWidth('lg');
  const { pathname } = useLocation();

  // 좁은 화면에서는 애초에 프레임이 화면을 꽉 채우므로 구분이 의미 없다.
  const isFluid = isDesktop && isConsolePath(pathname);

  return (
    <div css={styles.viewport}>
      {isDesktop && <GlobalNav fluid={isFluid} />}
      <div css={styles.row}>
        <div css={[styles.frame, isFluid && styles.frameFluid]}>
          <main css={styles.main}>
            <Outlet />
          </main>
          {!isDesktop && <BottomTab />}
        </div>
      </div>
    </div>
  );
}

const styles = {
  /*
    전역 CSS(global.css)가 `html, body` 를 뷰포트 높이 + `overflow: hidden` 으로 잠근다(관리자 셸 계승).
    따라서 문서 스크롤이 존재하지 않으므로 **이 셸이 내부 스크롤 컨테이너를 직접 만들어야** 한다.
    없으면 뷰포트보다 긴 페이지가 조용히 잘린다(클래스 상세에서 실제로 하단 섹션이 사라졌다).
  */
  viewport: css`
    display: flex;
    /* 전역 네비게이션이 프레임 위에 가로로 꽉 차게 얹히므로 세로 축으로 쌓는다. */
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    background: ${TOKEN.bgMuted};
  `,
  /*
    프레임을 가운데 놓는 줄. 남은 높이를 전부 차지하되 `min-height: 0` 으로 풀어야
    안쪽 스크롤이 산다(아래 main 과 같은 이유 — flex 아이템 기본값이 콘텐츠 높이만큼 버틴다).
    자식은 기본 stretch 라 프레임이 이 줄의 높이를 그대로 받는다.
  */
  row: css`
    display: flex;
    flex: 1;
    min-height: 0;
    justify-content: center;
  `,
  frame: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    ${APP_FRAME_CSS}
    min-height: 0;
    background: ${TOKEN.bgApp};
  `,
  /*
    `max-width: none` 을 평범한 선언으로 두면 먹지 않는다 — APP_FRAME_CSS 의 미디어쿼리 블록이
    직렬화될 때 뒤로 밀려 나중에 오는 규칙이 되기 때문이다(같은 클래스·같은 특이도라 순서가 이긴다).
    같은 조건의 미디어쿼리 안에서 덮어야 확실하다.
  */
  frameFluid: css`
    ${breakpoint.lg} {
      max-width: none;
    }
  `,
  main: css`
    flex: 1;
    /* flex 아이템 기본 min-height:auto 는 콘텐츠 높이만큼 버텨 스크롤이 생기지 않는다 — 반드시 0 으로 푼다. */
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overscroll-behavior: contain;
  `,
};

export default AppLayout;
